import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError, type CurrentUser } from '../api';
import { Alert, Button, Card, Flash } from '../components/ui';
import { IconClock } from '../components/icons';
import { NhcLogo } from '../components/NhcLogo';

/**
 * Signed in, no role. Straight from the process diagram: "Users can sign up,
 * but have no access to anything until roles are assigned by IT."
 *
 * Administrators were emailed when this account first appeared, and are
 * again each time the person comes back while still waiting. "Remind IT" is
 * for the person who does not want to wait for that: it sends now, at most
 * once an hour, and says so either way.
 */
export function PendingAccess({ user }: { user: CurrentUser }) {
  const [flash, setFlash] = useState<string | null>(null);

  const remind = useMutation({
    mutationFn: () => api.remindAccess(),
    onSuccess: ({ sent, nextAt }) => {
      setFlash(
        sent
          ? 'IT has been emailed that you are waiting.'
          : `IT was reminded recently. You can send another reminder after ${
              nextAt ? new Date(nextAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'a while'
            }.`,
      );
    },
  });

  async function signOut() {
    const { redirectTo } = await api.logout();
    window.location.href = redirectTo;
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg">
        <NhcLogo alt="New Horizon Copper" className="mb-8 h-9 w-auto self-start" />

        <Card className="p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-copper-100 text-copper-700 dark:bg-copper-900/50 dark:text-copper-300">
            <IconClock />
          </div>

          <h1 className="mt-5 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            You're signed in, {user.displayName.split(' ')[0]}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            Your account exists, but it has no role yet, so there is nothing here for you to do. An
            administrator at Micro Tech Namibia needs to assign you one. That is deliberate: nobody
            reaches enrolment requests until IT grants access. They have been told you are waiting.
          </p>

          <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 border-t border-slate-200 pt-6 text-sm dark:border-slate-800">
            <dt className="text-slate-500 dark:text-slate-400">Signed in as</dt>
            <dd className="text-slate-900 dark:text-slate-100">{user.email}</dd>
            <dt className="text-slate-500 dark:text-slate-400">Status</dt>
            <dd className="text-slate-900 dark:text-slate-100">Awaiting role assignment</dd>
          </dl>

          {flash && (
            <div className="mt-6">
              <Flash message={flash} onDismiss={() => setFlash(null)} />
            </div>
          )}
          {remind.isError && (
            <div className="mt-6">
              <Alert>{remind.error instanceof ApiError ? remind.error.message : 'Could not send the reminder.'}</Alert>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => window.location.reload()}>Check again</Button>
            <Button variant="secondary" onClick={() => remind.mutate()} disabled={remind.isPending}>
              {remind.isPending ? 'Sending…' : 'Remind IT'}
            </Button>
            <Button variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </Card>

        <p className="mt-4 px-1 text-xs text-slate-500 dark:text-slate-400">
          Once your role is assigned, reload this page; no need to sign in again.
        </p>
      </div>
    </div>
  );
}
